import type { LinearGradientProps } from 'expo-linear-gradient';
import type { CSSProperties } from 'react';

// LinearGradientPoint can be either {x, y} object or [x, y] array
type GradientPointObject = { x: number; y: number };
type GradientPointArray = readonly [number, number];
type GradientPoint = GradientPointObject | GradientPointArray;

/**
 * Type guard to check if point is an array format
 */
function isArrayPoint(point: GradientPoint): point is GradientPointArray {
  return Array.isArray(point);
}

/**
 * Helper to get x coordinate from a gradient point
 * Handles both object {x, y} and array [x, y] formats
 */
function getPointX(point: GradientPoint): number {
  if (isArrayPoint(point)) {
    return point[0];
  }
  return point.x;
}

/**
 * Helper to get y coordinate from a gradient point
 * Handles both object {x, y} and array [x, y] formats
 */
function getPointY(point: GradientPoint): number {
  if (isArrayPoint(point)) {
    return point[1];
  }
  return point.y;
}

/**
 * Converts expo-linear-gradient configuration to CSS linear-gradient format
 * @param gradientConfig - The expo-linear-gradient configuration
 * @returns CSS style object with backgroundImage property set to the linear gradient
 */
export function convertExpoGradientToCss(
  gradientConfig?: LinearGradientProps
): CSSProperties {
  if (!gradientConfig) return {};

  const { colors, locations, start, end } = gradientConfig;

  if (!colors || !colors.length) return {};

  // Calculate direction based on start and end points
  let direction = '180deg'; // default top to bottom

  if (start && end) {
    const startX = getPointX(start as GradientPoint);
    const startY = getPointY(start as GradientPoint);
    const endX = getPointX(end as GradientPoint);
    const endY = getPointY(end as GradientPoint);

    if (startX === endX) {
      // Vertical gradient
      direction = startY < endY ? '180deg' : '0deg';
    } else if (startY === endY) {
      // Horizontal gradient
      direction = startX < endX ? '90deg' : '270deg';
    } else {
      // Diagonal gradient
      if (startX < endX && startY < endY) {
        direction = '135deg'; // top-left to bottom-right
      } else if (startX > endX && startY < endY) {
        direction = '225deg'; // top-right to bottom-left
      } else if (startX < endX && startY > endY) {
        direction = '45deg'; // bottom-left to top-right
      } else {
        direction = '315deg'; // bottom-right to top-left
      }
    }
  }

  // Generate color stops
  const colorStops = colors.map((color, index) => {
    const colorStr = String(color);
    if (locations && locations[index] !== undefined) {
      return `${colorStr} ${locations[index] * 100}%`;
    }
    // Distribute colors evenly if no locations provided
    // Handle the case where colors.length is 1 to avoid division by zero
    return colors.length > 1 
      ? `${colorStr} ${(index / (colors.length - 1)) * 100}%`
      : `${colorStr} 0%`;
  });

  return {
    backgroundImage: `linear-gradient(${direction}, ${colorStops.join(', ')})`,
  };
}
